import React, { useState } from "react";
import { Grid, Dialog, IconButton, InputAdornment } from "@mui/material";
import {
  TextInput,
  AutocompleteInput,
  useGetList,
  required,
} from "react-admin";
import { CreateForm } from "../../../Layout/CreateForm";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const CreateUserForm = ({ handleClose }) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedRolePrecedence, setSelectedRolePrecedence] = useState(null);

  const { data: roleData, isPending: isRolesPending } = useGetList(
    "roles",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "createdAt", order: "ASC" },
      filter: { notSelf: true },
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const { data: userData, isPending: isUsersPending } = useGetList(
    "usersDropdown",
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "roleName", order: "ASC" },
      filter: { isPlayer: false },
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const filteredUsers = userData?.filter(
    (ele) => ele.rolePrecedence === selectedRolePrecedence - 1
  );

  const validateName = (value) => {
    if (!value) return "Required";
    if (!/^[A-Za-z]+$/.test(value)) return "Name can only contain alphabets";
    return undefined;
  };

  const validateUsername = (value) => {
    if (!value) return "Required";
    if (value.length < 3 || value.length > 15)
      return "Username must be between 3 and 15 characters";
    if (/^[^a-zA-Z0-9]+$/.test(value))
      return "Username cannot contain only special characters";
    return undefined;
  };

  const validateEmail = (value) => {
    if (!value) return undefined;
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(value)) return "Enter a valid email address";
    return undefined;
  };

  const validatePhone = (value) => {
    if (!value) return undefined;
    if (!/^\d{10}$/.test(value))
      return "Phone number must be exactly 10 digits";
    return undefined;
  };

  const validatePassword = (value) => {
    if (!value) return "Required";
    if (value.length < 6) return "Password must be at least 6 characters";
    return undefined;
  };

  const validateConfirmPassword = (password) => (value) => {
    if (!value) return "Required";
    if (value.length < 6) return "Password must be at least 6 characters";
    if (value !== password) return "Passwords do not match";
    return undefined;
  };

  return (
    <CreateForm
      title="Create New User"
      handleClose={handleClose}
      resource="users"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextInput
            id="name"
            name="name"
            label="Name"
            fullWidth
            validate={[required(), validateName]}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^A-Za-z]/g, "");
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="username"
            name="username"
            label="Username"
            fullWidth
            validate={[required(), validateUsername]}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="email"
            name="email"
            label="Email"
            fullWidth
            validate={[validateEmail]}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            id="phone"
            name="phone"
            label="Phone"
            fullWidth
            validate={[validatePhone]}
            inputProps={{ maxLength: 10, minLength: 10 }}
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, "");
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <AutocompleteInput
            label="Role"
            id="Role"
            source="role"
            choices={
              isRolesPending ? [{ id: "Null", name: "Loading" }] : roleData
            }
            optionText="name"
            optionValue="id"
            resettable
            emptyText="Select Role"
            validate={[required()]}
            onChange={(value) => {
              const selectedRole = roleData?.find((role) => role.id === value);
              setSelectedRolePrecedence(selectedRole?.precedence);
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <AutocompleteInput
            label="Manager"
            id="manager"
            source="manager"
            choices={
              isUsersPending ? [{ id: "Null", name: "Loading" }] : filteredUsers
            }
            optionText={(choice) => `${choice.username} (${choice.roleName})`}
            optionValue="id"
            resettable
            emptyText="Current User"
            validate={[required()]}
          />
        </Grid>

        <Grid item xs={12}>
          <TextInput
            id="password"
            name="password"
            label="Password"
            type={showPassword ? "text" : "password"}
            fullWidth
            validate={[required(), validatePassword]}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextInput
            id="confirmpassword"
            name="confirmpassword"
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            validate={[required(), validateConfirmPassword(password)]}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
    </CreateForm>
  );
};

export const NewUserFormDialog = ({ open, handleClose }) => {
  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="sm"
      open={open}
      onClose={handleClose}
      onClick={handleDialogClick}
    >
      <CreateUserForm open={open} handleClose={handleClose} />
    </Dialog>
  );
};
